import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import {
  Avatar,
  IconLocation,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../components';
import LocationAutocompleteInput from '../../../components/LocationAutocompleteInput/LocationAutocompleteInput';
import BookingDateRangeFilter from '../BookingDateRangeFilter/BookingDateRangeFilter';
import GuestPicker from '../GuestPicker/GuestPicker';
import css from './SearchPageTopbar.module.css';

// ── Nav link sub-components (unchanged) ─────────────────────────────────────

const CreateListingLink = () => (
  <NamedLink name="NewListingPage" className={`${css.navLink} ${css.navLinkHighlight}`}>
    <span className={css.navLinkLabel}>
      <FormattedMessage id="TopbarDesktop.createListing" />
    </span>
  </NamedLink>
);

const MyBookingsLink = () => (
  <NamedLink className={css.navLink} name="MyBookingsBasePage">
    <span className={css.navLinkLabel}>
      <FormattedMessage id="TopbarDesktop.myBookings" />
    </span>
  </NamedLink>
);

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.navLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.navLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentUser, onLogout, intl }) => (
  <Menu skipFocusOnNavigation={true}>
    <MenuLabel
      className={css.profileMenuLabel}
      isOpenClassName={css.profileMenuIsOpen}
      ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
    >
      <Avatar className={css.avatar} user={currentUser} disableProfileLink />
    </MenuLabel>
    <MenuContent className={css.profileMenuContent}>
      <MenuItem key="ProfileSettingsPage">
        <NamedLink className={css.menuLink} name="ProfileSettingsPage">
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
        </NamedLink>
      </MenuItem>
      <MenuItem key="AccountSettingsPage">
        <NamedLink className={css.menuLink} name="AccountSettingsPage">
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
        </NamedLink>
      </MenuItem>
      <MenuItem key="logout">
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.logout" />
        </InlineTextButton>
      </MenuItem>
    </MenuContent>
  </Menu>
);

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Desktop-only custom topbar for the Search page.
 * Logo | functional search bar (Location · Dates · Keyword) | right nav.
 * Hidden on mobile — the standard TopbarContainer is shown there instead.
 *
 * @param {string}   props.address            Current location string from URL params
 * @param {string}   props.dates              Current dates string from URL params
 * @param {string}   props.keywords           Current keyword string from URL params
 * @param {Object}   props.validQueryParams   Full current URL query params (passed to date filter)
 * @param {Function} props.onSearch           Called with updated URL param object on any segment change
 * @param {Function} props.onOpenFilters      Called to open the filter panel (kept for external use)
 */
const SearchPageTopbar = ({
  address,
  dates,
  keywords,
  validQueryParams,
  onSearch,
  onOpenFilters,
  currentUser,
  isAuthenticated,
  notificationCount = 0,
  onLogout,
  intl,
  showCreateListingsLink,
  currentUserHasListings,
}) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  // ── Location segment state ───────────────────────────────────────────────
  const [locationValue, setLocationValue] = useState({
    search: address || '',
    predictions: [],
    selectedPlace: null,
  });

  // Sync input text when address URL param changes (e.g. browser back/forward)
  useEffect(() => {
    setLocationValue(prev => ({ ...prev, search: address || '', selectedPlace: null }));
  }, [address]);

  const handleLocationChange = value => {
    setLocationValue(value);
    if (value?.selectedPlace) {
      const { search } = value;
      const { origin, bounds } = value.selectedPlace;
      onSearch({ address: search, bounds, ...(origin ? { origin: `${origin.lat},${origin.lng}` } : {}) });
    }
  };

  // ── Keyword segment state ────────────────────────────────────────────────
  const [keywordValue, setKeywordValue] = useState(keywords || '');

  useEffect(() => {
    setKeywordValue(keywords || '');
  }, [keywords]);

  const handleKeywordKeyDown = e => {
    if (e.key === 'Enter') {
      const addressMaybe = address ? { address } : {};
      const boundsMaybe = validQueryParams?.bounds ? { bounds: validQueryParams.bounds } : {};
      onSearch({ ...addressMaybe, ...boundsMaybe, keywords: keywordValue });
    }
  };

  // ── Dates segment handler ────────────────────────────────────────────────
  // Preserve address/bounds from URL since validQueryParams strips them
  const handleDatesSubmit = params => {
    const addressMaybe = address ? { address } : {};
    const boundsMaybe = validQueryParams?.bounds ? { bounds: validQueryParams.bounds } : {};
    onSearch({ ...addressMaybe, ...boundsMaybe, ...(params || {}) });
  };

  // ── Nav elements ─────────────────────────────────────────────────────────
  const createListingLinkMaybe = showCreateListingsLink ? <CreateListingLink /> : null;
  const myBookingsLinkMaybe = authenticatedOnClientSide ? <MyBookingsLink /> : null;
  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;
  const profileMenuMaybe =
    authenticatedOnClientSide && intl ? (
      <ProfileMenu currentUser={currentUser} onLogout={onLogout} intl={intl} />
    ) : null;
  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.navLink}>
      <span className={css.navLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.navLink}>
      <span className={css.navLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  return (
    <header className={css.root}>
      {/* Logo */}
      <LinkedLogo rootClassName={css.logoLink} layout="desktop" />

      {/* Functional search bar */}
      <div className={css.searchPill} role="search" aria-label="Search">

        {/* Location segment */}
        <div className={css.locationSegment}>
          <LocationAutocompleteInput
            id="topbar-location"
            rootClassName={css.locationInputRoot}
            iconClassName={css.locationIconWrapper}
            CustomIcon={() => <IconLocation rootClassName={css.locationIcon} />}
            inputClassName={css.locationInput}
            predictionsClassName={css.locationPredictions}
            placeholder="Location"
            useDarkText={true}
            disableKeywordSuggestions={true}
            geocoderTypes={['place', 'region', 'country']}
            closeOnBlur={true}
            hideErrorMessage
            input={{
              name: 'location',
              value: locationValue,
              onChange: handleLocationChange,
              onBlur: () => {},
              onFocus: () => {},
            }}
            meta={{ valid: true, invalid: false, touched: false, error: undefined }}
          />
        </div>

        <span className={css.divider} aria-hidden="true" />

        {/* Dates segment — BookingDateRangeFilter with popup calendar */}
        <div className={css.datesSegment}>
          <BookingDateRangeFilter
            id="topbar-dates"
            label="Add dates"
            rootClassName={css.datesFilterRoot}
            labelClassName={css.datesTrigger}
            labelSelectedClassName={css.datesTriggerSelected}
            queryParamNames={['dates']}
            initialValues={validQueryParams || {}}
            onSubmit={handleDatesSubmit}
          />
        </div>

        <span className={css.divider} aria-hidden="true" />

        {/* Who? guest picker */}
        <GuestPicker className={css.guestPickerWrapper} onSearch={onSearch} />

        <span className={css.divider} aria-hidden="true" />

        {/* Keyword segment */}
        <input
          type="text"
          className={css.keywordInput}
          placeholder="What are you looking for?"
          value={keywordValue}
          onChange={e => setKeywordValue(e.target.value)}
          onKeyDown={handleKeywordKeyDown}
          aria-label="Keyword search"
        />
      </div>

      {/* Right nav */}
      <nav className={css.nav} aria-label="User navigation">
        {createListingLinkMaybe}
        {myBookingsLinkMaybe}
        {inboxLinkMaybe}
        {profileMenuMaybe}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </nav>
    </header>
  );
};

export default SearchPageTopbar;
